import { useCallback, useEffect, useState } from 'react'
import { extractErrorMessage, success, failure } from 'utils/errorHandling'
import useInnovaAxios from 'components/common/hooks/useInnovaAxios'

export function useRoles() {
  const getRolesRequest = useInnovaAxios({ url: '/admin/user/roles' })
  const getProductKeysRequest = useInnovaAxios({ url: '/admin/getAllProductKeys' })
  const getFeaturesRequest = useInnovaAxios({ url: '/admin/getFeaturesByOrgWithDesc' })
  const getOrganizationsRequest = useInnovaAxios({ url: '/admin/getOrgListByUser' })
  const createRoleRequest = useInnovaAxios({ url: '/admin/user/role/save' })
  const updateRoleRequest = useInnovaAxios({ url: '/admin/user/role/save' })
  const copyRoleRequest = useInnovaAxios({ url: '/admin/user/role/duplicate' })
  const deleteRoleRequest = useInnovaAxios({ url: '/admin/user/role/delete' })

  const getOperators = useInnovaAxios({ url: '/admin/getOperatorList' })
  const getOperatorAccess = useInnovaAxios({ url: '/admin/role/getOperatorAccess' })
  const updateOperatorAccess = useInnovaAxios({ url: '/admin/role/updateOperatorAccess' })

  const saveFeatureAccessRequest = useInnovaAxios({ url: '/admin/user/role/saveFeatures' })

  const [roles, setRoles] = useState([])
  const [productKeys, setProductKeys] = useState([])
  const [features, setFeatures] = useState([])
  const [organizations, setOrganizations] = useState([])

  const [isLoading, setIsLoading] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isCopying, setIsCopying] = useState(false)
  const [isLoadingAccess, setIsLoadingAccess] = useState(false)
  const [isLoadingProductKeys, setIsLoadingProductKeys] = useState(false)
  const [isLoadingProductFeatures, setIsLoadingProductFeatures] = useState(false)
  const [isLoadingOrganizations, setIsLoadingOrganiztions] = useState(false)
  const [isSavingOperatorAccess, setIsSavingOperatorAccess] = useState(false)
  const [isSavingFeatureAccess, setIsSavingFeatureAccess] = useState(false)

  /* ============================================================
     Fetch Product Keys
     ============================================================ */

  const fetchProductKeys = async () => {
    setIsLoadingProductKeys(true)
    try {
      const res = await getProductKeysRequest()

      if (res?.error) {
        return failure(extractErrorMessage(res.error, 'Failed to fetch product keys'))
      }

      if (Array.isArray(res?.data)) {
        setProductKeys(res.data)
      }

      return success('Product keys loaded')
    } finally {
      setIsLoadingProductKeys(false)
    }
  }

  /* ============================================================
     Fetch Roles
     ============================================================ */

  const fetchRoles = useCallback(async () => {
    setIsLoading(true)
    try {
      const res = await getRolesRequest()

      if (res?.error) {
        return failure(extractErrorMessage(res.error, 'Failed to fetch roles'))
      }

      if (Array.isArray(res?.data)) {
        const sorted = [...res.data].sort(
          (a, b) => a.organization.localeCompare(b.organization) || a.name.localeCompare(b.name),
        )

        setRoles(sorted)
      }

      return success('Roles loaded')
    } finally {
      setIsLoading(false)
    }
  }, [getRolesRequest])

  /* ============================================================
     Features Access
     ============================================================ */

  const fetchFeatures = useCallback(async () => {
    setIsLoadingProductFeatures(true)
    try {
      const res = await getFeaturesRequest()

      if (res?.error) {
        return failure(extractErrorMessage(res.error, 'Failed to fetch features'))
      }

      if (Array.isArray(res?.data)) {
        setFeatures(res.data)
      }

      return success('Features loaded')
    } finally {
      setIsLoadingProductFeatures(false)
    }
  }, [getFeaturesRequest])

  const saveFeatureAccess = useCallback(
    async (selectedFeatures, role) => {
      if (!role) return failure('Invalid role')
      if (!Array.isArray(selectedFeatures)) return failure('Invalid features list')
      if (isSavingFeatureAccess) return failure('Operation already in progress')

      setIsSavingFeatureAccess(true)
      try {
        const res = await saveFeatureAccessRequest({ id: role.id, featureIds: selectedFeatures.join(',') })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to fetch features'))
        }

        setRoles((prev) =>
          prev.map((u) =>
            u.id === role.id ? { ...u, roleAttributes: { ...u.roleAttributes, featureId: selectedFeatures } } : u,
          ),
        )

        return success('Features access saved')
      } finally {
        setIsSavingFeatureAccess(false)
      }
    },
    [saveFeatureAccessRequest, isSavingFeatureAccess],
  )

  /* ============================================================
     Fetch Organizations
     ============================================================ */

  const fetchOrganizations = useCallback(async () => {
    setIsLoadingOrganiztions(true)
    try {
      const res = await getOrganizationsRequest()

      if (res?.error) {
        return failure(extractErrorMessage(res.error, 'Failed to fetch organizations'))
      }

      if (Array.isArray(res?.data)) {
        setOrganizations([...res.data].sort((a, b) => a.localeCompare(b)))
      }

      return success('Organizations loaded')
    } finally {
      setIsLoadingOrganiztions(false)
    }
  }, [getOrganizationsRequest])

  /* ============================================================
       Utilities
     ============================================================ */

  const getRolesWithAlerts = useCallback(() => {
    if (!Array.isArray(roles)) return []
    return roles.map((role) => (roles?.alerts?.notifications !== null ? role : null)).filter((v) => v !== null)
  }, [roles])

  const createOrgFeatureList = useCallback(
    (organization) => {
      if (!organization) return []
      if (!Array.isArray(features)) return []
      if (!Array.isArray(productKeys)) return []

      let index = features.findIndex((element) => element.organization === organization)
      if (index < 0) return []

      let output = productKeys.map((product) => {
        return {
          header: product.description,
          productKey: product.key,
          data: [],
        }
      })

      features[index].features.forEach((feature) => {
        let productIndex = output.findIndex((element) => feature.featureId.includes(element.productKey))
        if (productIndex >= 0) output[productIndex].data.push({ id: feature.featureId, desc: feature.description })
      })

      return output
    },
    [features, productKeys],
  )

  /* ============================================================
   Operator Access
   ============================================================ */

  const fetchOperatorsForRole = useCallback(
    async (role) => {
      if (!role) return failure('Invalid role')

      setIsLoadingAccess(true)

      try {
        const res = await getOperators({
          roleId: role.id,
        })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to fetch operators'))
        }

        let operators = []
        if (Array.isArray(res.data)) {
          operators = res.data.map((op) => {
            return { id: op, desc: op }
          })
        }

        const resSelected = await getOperatorAccess({
          accessRoleId: role.id,
        })

        if (resSelected?.error) {
          return failure(extractErrorMessage(resSelected.error, 'Failed to fetch operator access'))
        }

        let selectedOperators = Array.isArray(resSelected?.data) ? resSelected.data : []

        return success('Operators access fetched', { operators, selectedOperators })
      } catch (err) {
        return failure(extractErrorMessage(err))
      } finally {
        setIsLoadingAccess(false)
      }
    },
    [getOperators, getOperatorAccess],
  )

  const saveOperatorAccess = useCallback(
    async (role, selectedOperators) => {
      if (!role) return failure('Invalid role')
      if (!Array.isArray(selectedOperators)) return failure('Invalid operator list')
      if (isSavingOperatorAccess) return failure('Operation already in progress')

      setIsSavingOperatorAccess(true)

      try {
        const res = await updateOperatorAccess({
          accessRoleId: role.id,
          operatorList: selectedOperators.join('|'),
        })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to update operator access'))
        }

        return success('Operator access updated')
      } catch (err) {
        return failure(extractErrorMessage(err))
      } finally {
        setIsSavingOperatorAccess(false)
      }
    },
    [updateOperatorAccess, isSavingOperatorAccess],
  )

  /* ============================================================
       Update Role
     ============================================================ */

  const updateRole = useCallback(
    async (form) => {
      if (!form?.id) return failure('Invalid role data')
      if (isUpdating) return failure('Update already in progress')

      setIsUpdating(true)
      try {
        const payload = {
          id: form.id,
          name: form.name,
          organization: form.organization,
          'roleAttributes.description': form.roleAttributes.description,
          'roleAttributes.featureId': form.roleAttributes.featureId,
          'roleAttributes.permissions.admin': form.roleAttributes.permissions.admin,
          'roleAttributes.permissions.canEdit': form.roleAttributes.permissions.canEdit,
          'roleAttributes.permissions.canDelete': form.roleAttributes.permissions.canDelete,
          'roleAttributes.permissions.canApprove': form.roleAttributes.permissions.canApprove,
          'roleAttributes.permissions.canCreate': form.roleAttributes.permissions.canCreate,
        }

        const res = await updateRoleRequest(payload)

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to update role'))
        }

        setRoles((prev) => prev.map((u) => (u.id === form.id ? { ...u, ...form } : u)))

        return success('Role updated')
      } finally {
        setIsUpdating(false)
      }
    },
    [updateRoleRequest, isUpdating],
  )

  /* ============================================================
       Add Role
     ============================================================ */

  const addRole = useCallback(
    async (form) => {
      setIsCreating(true)
      try {
        const payload = {
          name: form.name,
          organization: form.organization,
          'roleAttributes.description': form.roleAttributes.description,
          'roleAttributes.featureId': form.roleAttributes.featureId,
          'roleAttributes.permissions.admin': form.roleAttributes.permissions.admin,
          'roleAttributes.permissions.canEdit': form.roleAttributes.permissions.canEdit,
          'roleAttributes.permissions.canDelete': form.roleAttributes.permissions.canDelete,
          'roleAttributes.permissions.canApprove': form.roleAttributes.permissions.canApprove,
          'roleAttributes.permissions.canCreate': form.roleAttributes.permissions.canCreate,
        }

        const res = await createRoleRequest(payload)

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to create role'))
        }

        setRoles((prev) => {
          return [...prev, res.data].sort(
            (a, b) =>
              (a.organization ?? '').localeCompare(b.organization ?? '') || (a.name ?? '').localeCompare(b.name ?? ''),
          )
        })

        return success('Role created')
      } finally {
        setIsCreating(false)
      }
    },
    [createRoleRequest],
  )

  /* ============================================================
     Delete Role
     ============================================================ */

  const deleteRole = useCallback(
    async (role) => {
      if (!role) return failure('Invalid role data')

      const existingRole = roles.find((r) => r.id === role.id)
      if (!existingRole) return failure('Role not found')

      const orgRoleCount = roles.filter((r) => r.organization === existingRole.organization).length

      if (orgRoleCount <= 1) {
        return failure(`Cannot delete last role for ${existingRole.organization}`)
      }

      setIsDeleting(true)
      try {
        const res = await deleteRoleRequest({ id: existingRole.id })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Delete failed'))
        }

        setRoles((prev) => prev.filter((r) => r.id !== existingRole.id))

        return success('Role deleted')
      } finally {
        setIsDeleting(false)
      }
    },
    [deleteRoleRequest, roles],
  )

  /* ============================================================
       Copy Role
     ============================================================ */

  const copyRole = useCallback(
    async (role) => {
      if (!role) return failure('Invalid role data')
      setIsCopying(true)
      try {
        const payload = { id: role.id }

        const res = await copyRoleRequest(payload)

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to copy role'))
        }

        setRoles((prev) => {
          return [...prev, res.data].sort(
            (a, b) =>
              (a.organization ?? '').localeCompare(b.organization ?? '') || (a.name ?? '').localeCompare(b.name ?? ''),
          )
        })

        return success('Role copied')
      } finally {
        setIsCopying(false)
      }
    },
    [copyRoleRequest],
  )

  useEffect(() => {
    fetchRoles()
    fetchProductKeys()
    fetchFeatures()
    fetchOrganizations()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    isLoading,
    isUpdating,
    isCreating,
    isDeleting,
    isCopying,
    isLoadingAccess,
    isLoadingProductKeys,
    isLoadingProductFeatures,
    isLoadingOrganizations,
    isSavingOperatorAccess,
    isSavingFeatureAccess,
    roles,
    organizations,
    features,
    getRolesWithAlerts,
    fetchRoles,
    fetchOperatorsForRole,
    createOrgFeatureList,
    saveOperatorAccess,
    saveFeatureAccess,
    updateRole,
    addRole,
    deleteRole,
    copyRole,
  }
}

export default useRoles
